import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import StatusChip from "./StatusChip"
import Avatar from "./Avatar"
import { dateFmt } from "../helpers/format"

const Meta = ({ icon, label, link, fallback = "—" }) => {
  const content = label || fallback
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600">
      <FontAwesomeIcon icon={icon} className="text-slate-400 w-3.5 h-3.5" />
      {link ? (
        <a href={link} target="_blank" rel="noopener" className="hover:text-slate-900 hover:underline">{content}</a>
      ) : (
        <span>{content}</span>
      )}
    </span>
  )
}

const ColdMeta = ({ project }) => {
  const [open, setOpen] = React.useState(false)
  if (!project.department && !project.trade) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800"
      >
        <FontAwesomeIcon icon="circle-info" className="text-slate-400 w-3.5 h-3.5" />
        {I18n.t("projects.header.details")}
      </button>
    )
  }

  return (
    <>
      {project.department && (
        <Meta icon="sitemap" label={`${project.department.code} · ${project.department.name}`} />
      )}
      {project.trade && (
        <Meta icon="tools" label={`${project.trade.code} · ${project.trade.name}`} />
      )}
    </>
  )
}

const Header = ({ project, editPath }) => (
  <div className="border-b border-slate-200 bg-white">
    {/* One left edge for the whole page: the title, the tabs and the section
        headings all start here. The back arrow used to sit in the flow and push
        the title 80 px in, on its own margin, while everything below it lined up
        somewhere else. It belongs with the crumb it repeats. */}
    {/* Written out: Bootstrap also defines .pt-5 and .pb-4, with values of its
        own, so the two edges of this block were never the same. 19 at the
        bottom, not 20: the block's own rule sits in that gap and counts. */}
    <div className="pl-[44px] pr-7 pt-[20px] pb-[19px]">
      <div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[12px] text-slate-500 mb-1.5 -ml-[26px]">
            <a
              href      = "/projects"
              title     = {I18n.t("projects.header.back_to_projects")}
              className = "shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] rounded text-slate-400 hover:text-slate-900"
            >
              <FontAwesomeIcon icon="arrow-left" className="text-[11px]" />
            </a>
            <a href="/projects" className="hover:text-slate-900">{I18n.t("projects.projects")}</a>
          </div>

          <div className="flex items-center justify-between gap-4">
            <h1 className="text-[22px] font-semibold tracking-tight text-slate-900 truncate">
              {project.name}
            </h1>
            <div className="flex items-center gap-2 shrink-0">
              {/* CEO/VP synthesis — HTML preview in a new tab, or the PDF. */}
              <a
                href={`/projects/${project.id}/executive_synthesis`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-indigo-200 text-[12.5px] font-medium text-indigo-700 hover:bg-indigo-50"
                title={I18n.t("projects.executive_synthesis.preview_hint", { defaultValue: "Synthèse exécutive du chantier" })}
              >
                <FontAwesomeIcon icon="eye" />
                {I18n.t("projects.executive_synthesis.preview_cta", { defaultValue: "Aperçu" })}
              </a>
              <a
                href={`/projects/${project.id}/executive_synthesis.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
                title={I18n.t("projects.executive_synthesis.print_hint", { defaultValue: "Télécharger la synthèse en PDF" })}
              >
                <FontAwesomeIcon icon="file-pdf" />
                {I18n.t("projects.executive_synthesis.print_cta", { defaultValue: "PDF" })}
              </a>
            </div>
          </div>

          {/* The status belongs with the dates it qualifies, not glued to the
              title where it competed with it for the eye. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
            {project.partner_name && <Meta icon="handshake" label={project.partner_name} />}
            <Meta icon="user-circle" label={project.client?.name} link={project.client?.path} fallback={I18n.t("projects.header.no_client")} />
            {project.site && <Meta icon="building" label={project.site.name} link={project.site.path} />}
            <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-600">
              <Avatar name={project.manager?.name} size="sm" ring={false} />
              <span>{project.manager?.name || I18n.t("projects.header.no_manager")}</span>
            </span>
            <Meta icon="calendar" label={I18n.t("projects.summary.period_range", { start: dateFmt(project.start_date), end: dateFmt(project.end_date) })} />
            <StatusChip status={project.status} label={project.status_label} />
            <ColdMeta project={project} />
          </div>
        </div>
      </div>
    </div>
  </div>
)

export default Header
